/**
 * MUESTRAS PAGE — Cola de Atención
 *
 * Vista principal del módulo de toma de muestras.
 * Muestra las órdenes EN_EXTRACCION de la sucursal activa,
 * ordenadas por prioridad y hora de ingreso.
 *
 * Requirements: M4.1, M4.2, M4.15
 */

import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  TestTubes,
  RefreshCw,
  AlertCircle,
  Clock,
  FlaskConical,
  Monitor,
  BarChart3,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TipoServicioBadge } from "@/components/tipo-servicio-badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

import { useSucursal } from "@/contexts/sucursal-context";
import { useApiError } from "@/hooks/use-api-error";
import {
  useColaAtencion,
  useAbrirAtencion,
  type ColaAtencionItem,
  type TipoServicio,
} from "@/hooks/use-muestras";
import { usePuntosAtencionActivos } from "@/hooks/use-turnos";
import { sortCola } from "./utils/cola-sorting";

import { AbrirAtencionDialog } from "./components/abrir-atencion-dialog";
import { PrioridadBadge } from "./components/prioridad-badge";
import { ColaAtencionFilters } from "./components/cola-atencion-filters";
import { EstadisticasMuestras } from "./components/estadisticas-muestras";

export default function MuestrasPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { sucursalActiva } = useSucursal();
  const { getErrorMessage } = useApiError();

  const [tipoServicioFiltro, setTipoServicioFiltro] = useState<TipoServicio | undefined>(undefined);
  const [ordenSeleccionada, setOrdenSeleccionada] = useState<ColaAtencionItem | null>(null);
  const [isAbrirDialogOpen, setIsAbrirDialogOpen] = useState(false);

  const sucursalId = sucursalActiva?.id ?? 0;

  // ─── Mi Estación de Extracción ───────────────────────────────────────────
  // Persiste en localStorage igual que "Mi Punto de Atención" en Turnos
  const [miEstacionId, setMiEstacionId] = useState<number | null>(() => {
    if (!sucursalActiva?.id) return null;
    const saved = localStorage.getItem(`miEstacionExtraccion_${sucursalActiva.id}`);
    return saved ? Number(saved) : null;
  });

  // Sincronizar cuando cambia la sucursal
  useEffect(() => {
    if (sucursalActiva?.id) {
      const saved = localStorage.getItem(`miEstacionExtraccion_${sucursalActiva.id}`);
      setMiEstacionId(saved ? Number(saved) : null);
    }
  }, [sucursalActiva?.id]);

  const handleSetMiEstacion = useCallback(
    (id: number | null) => {
      setMiEstacionId(id);
      if (sucursalActiva?.id) {
        if (id) {
          localStorage.setItem(`miEstacionExtraccion_${sucursalActiva.id}`, String(id));
        } else {
          localStorage.removeItem(`miEstacionExtraccion_${sucursalActiva.id}`);
        }
      }
    },
    [sucursalActiva?.id]
  );

  // Puntos de atención de tipo EXTRACCION de la sucursal activa
  const { data: todosLosPuntos = [] } = usePuntosAtencionActivos(sucursalId, {
    enabled: sucursalId > 0,
  });
  const puntosExtraccion = todosLosPuntos.filter((p) => p.tipo === "EXTRACCION");

  // Objeto del punto seleccionado
  const miEstacion = puntosExtraccion.find((p) => p.id === miEstacionId) ?? null;

  // ─── Cola ────────────────────────────────────────────────────────────────
  const {
    data: colaData,
    isLoading,
    error,
    refetch,
  } = useColaAtencion(sucursalId, tipoServicioFiltro, {
    enabled: sucursalId > 0,
  });

  const abrirAtencionMutation = useAbrirAtencion({
    onSuccess: (atencion) => {
      toast.success(t("muestras.atencion.abierta_exitosamente"));
      setIsAbrirDialogOpen(false);
      setOrdenSeleccionada(null);
      navigate(`/muestras/atencion/${atencion.id}`);
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const cola = colaData ? sortCola(colaData) : [];

  const handleAtender = (item: ColaAtencionItem) => {
    setOrdenSeleccionada(item);
    setIsAbrirDialogOpen(true);
  };

  const formatHoraIngreso = (isoDate: string) => {
    return new Date(isoDate).toLocaleTimeString("es-HN", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getSexoLabel = (sexo: string) => {
    if (sexo === "M") return t("common.masculino");
    if (sexo === "F") return t("common.femenino");
    return sexo;
  };

  if (!sucursalId) {
    return (
      <div className="p-6">
        <div className="flex items-center gap-2 rounded-md border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-800">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>{t("muestras.cola.selecciona_sucursal")}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <TestTubes className="h-6 w-6 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">{t("muestras.cola.titulo")}</h1>
            <p className="text-sm text-muted-foreground">{t("muestras.cola.subtitulo")}</p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* Selector de estación — solo puntos tipo EXTRACCION */}
          <div className="flex items-center gap-2">
            <Monitor className="h-4 w-4 text-muted-foreground shrink-0" />
            <Select
              value={miEstacionId?.toString() ?? "none"}
              onValueChange={(v) => handleSetMiEstacion(v === "none" ? null : Number(v))}
            >
              <SelectTrigger
                className={`w-[180px] text-sm ${!miEstacionId ? "text-muted-foreground border-dashed" : "border-primary"}`}
              >
                <SelectValue placeholder={t("muestras.cola.seleccionar_estacion")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">
                  <span className="text-muted-foreground">{t("muestras.cola.sin_estacion")}</span>
                </SelectItem>
                {puntosExtraccion.map((p) => (
                  <SelectItem key={p.id} value={p.id.toString()}>
                    {p.nombre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isLoading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? "animate-spin" : ""}`} />
            {t("common.refresh")}
          </Button>
        </div>
      </div>

      {/* Alerta si no hay estación seleccionada */}
      {!miEstacion && puntosExtraccion.length > 0 && (
        <div className="flex items-center gap-2 rounded-md border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-800">
          <Monitor className="h-4 w-4 shrink-0" />
          <span>{t("muestras.cola.selecciona_estacion_aviso")}</span>
        </div>
      )}

      {/* Tabs: Cola / Estadísticas */}
      <Tabs defaultValue="cola">
        <TabsList>
          <TabsTrigger value="cola">
            <TestTubes className="h-4 w-4 mr-1.5" />
            {t("muestras.cola.titulo")}
          </TabsTrigger>
          <TabsTrigger value="estadisticas">
            <BarChart3 className="h-4 w-4 mr-1.5" />
            {t("muestras.estadisticas.titulo")}
          </TabsTrigger>
        </TabsList>

        {/* ── Tab Cola ─────────────────────────────────────────────────── */}
        <TabsContent value="cola" className="mt-4 space-y-4">
          {/* Filtros */}
          <ColaAtencionFilters
            tipoServicio={tipoServicioFiltro}
            onTipoServicioChange={setTipoServicioFiltro}
          />

          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span>{getErrorMessage(error as Error)}</span>
            </div>
          )}

          {/* Cola */}
          <div className="space-y-2">
            {isLoading ? (
              <Card>
                <CardContent className="p-8 text-center text-muted-foreground">
                  {t("common.loading")}
                </CardContent>
              </Card>
            ) : cola.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center text-muted-foreground">
                  <TestTubes className="h-10 w-10 mx-auto mb-2 opacity-30" />
                  <p>{t("muestras.cola.sin_pacientes")}</p>
                </CardContent>
              </Card>
            ) : (
              cola.map((item) => {
                const borderColor =
                  item.prioridad === "STAT"
                    ? "border-l-red-500"
                    : item.prioridad === "URGENTE"
                      ? "border-l-orange-400"
                      : "border-l-border";

                return (
                  <div
                    key={item.id}
                    className={`bg-card border border-l-4 ${borderColor} rounded-lg px-4 py-3 hover:bg-muted/40 transition-colors`}
                  >
                    <div className="flex items-center gap-3">
                      {/* Prioridad — solo desktop */}
                      <div className="hidden sm:block shrink-0">
                        <PrioridadBadge prioridad={item.prioridad} />
                      </div>

                      {/* Datos del paciente */}
                      <div className="flex-1 min-w-0">
                        {/* Nombre + prioridad inline en móvil */}
                        <div className="flex items-center gap-2">
                          <div className="sm:hidden shrink-0">
                            <PrioridadBadge prioridad={item.prioridad} />
                          </div>
                          <p className="font-semibold text-sm sm:text-base leading-tight truncate">
                            {item.paciente_nombre}
                          </p>
                        </div>

                        {/* Metadata */}
                        <div className="flex items-center gap-x-2 gap-y-0.5 mt-1 flex-wrap">
                          <span className="font-mono text-xs text-muted-foreground whitespace-nowrap">
                            {item.numero}
                          </span>
                          <span className="text-muted-foreground/50 text-xs">·</span>
                          <span className="text-xs text-muted-foreground whitespace-nowrap">
                            {item.paciente_edad}
                          </span>
                          <span className="text-muted-foreground/50 text-xs">·</span>
                          <span className="text-xs text-muted-foreground">
                            {getSexoLabel(item.paciente_sexo)}
                          </span>
                          <TipoServicioBadge tipo={item.tipo_servicio_derivado} />
                        </div>
                      </div>

                      {/* Exámenes + hora + botón — desktop */}
                      <div className="hidden sm:flex items-center gap-3 shrink-0">
                        <div className="flex flex-col items-center gap-0.5">
                          <div className="flex items-center gap-1 text-sm">
                            <FlaskConical className="h-3.5 w-3.5 text-muted-foreground" />
                            <span className="font-semibold">{item.examenes_pendientes}</span>
                          </div>
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Clock className="h-3 w-3" />
                            <span>{formatHoraIngreso(item.created_at)}</span>
                          </div>
                        </div>
                        {item.atencion_abierta_id ? (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-blue-500 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950 font-medium"
                            onClick={() =>
                              navigate(`/muestras/atencion/${item.atencion_abierta_id}`)
                            }
                          >
                            {t("muestras.cola.continuar")}
                          </Button>
                        ) : (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span>
                                  <Button
                                    size="sm"
                                    onClick={() => handleAtender(item)}
                                    disabled={!miEstacion}
                                    className="font-medium"
                                  >
                                    {t("muestras.cola.atender")}
                                  </Button>
                                </span>
                              </TooltipTrigger>
                              {!miEstacion && (
                                <TooltipContent>
                                  <p>{t("muestras.cola.selecciona_estacion_primero")}</p>
                                </TooltipContent>
                              )}
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </div>

                      {/* Exámenes + botón — móvil (botón compacto) */}
                      <div className="flex sm:hidden items-center gap-2 shrink-0">
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <FlaskConical className="h-3.5 w-3.5" />
                          <span className="font-semibold text-foreground">
                            {item.examenes_pendientes}
                          </span>
                        </div>
                        {item.atencion_abierta_id ? (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-blue-500 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950 font-medium text-xs px-2 h-8"
                            onClick={() =>
                              navigate(`/muestras/atencion/${item.atencion_abierta_id}`)
                            }
                          >
                            {t("muestras.cola.continuar")}
                          </Button>
                        ) : (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span>
                                  <Button
                                    size="sm"
                                    onClick={() => handleAtender(item)}
                                    disabled={!miEstacion}
                                    className="font-medium text-xs px-3 h-8"
                                  >
                                    {t("muestras.cola.atender")}
                                  </Button>
                                </span>
                              </TooltipTrigger>
                              {!miEstacion && (
                                <TooltipContent>
                                  <p>{t("muestras.cola.selecciona_estacion_primero")}</p>
                                </TooltipContent>
                              )}
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {!isLoading && cola.length > 0 && (
            <p className="text-xs text-muted-foreground text-center">
              {t("muestras.cola.n_pacientes", { count: cola.length })}
            </p>
          )}
        </TabsContent>

        {/* ── Tab Estadísticas ─────────────────────────────────────────── */}
        <TabsContent value="estadisticas" className="mt-4">
          <EstadisticasMuestras />
        </TabsContent>
      </Tabs>

      {/* Dialog de apertura de atención */}
      {ordenSeleccionada && miEstacion && (
        <AbrirAtencionDialog
          open={isAbrirDialogOpen}
          onOpenChange={setIsAbrirDialogOpen}
          orden={ordenSeleccionada}
          estacionNombre={miEstacion.nombre}
          onConfirm={(data: import("@/hooks/use-muestras").AbrirAtencionData) =>
            abrirAtencionMutation.mutate(data)
          }
          isLoading={abrirAtencionMutation.isPending}
        />
      )}
    </div>
  );
}
